import { computed, DestroyRef, DOCUMENT, inject, signal, type Signal } from '@angular/core';

import {
  CHECK_LONG_STROKE,
  CHECK_SHORT_STROKE,
  createLoaderMorph,
  easeInOutCubic,
  interpolatePath,
  type LoaderMorph,
} from './branded-loader-shapes';

const FINISH_PLAYBACK_RATE = 2.2;
const MORPH_MS = 500;
const POP_MS = 320;
const POP_SCALE = 0.1;
const FLASH_IN_MS = 250;
const FLASH_OUT_START_MS = MORPH_MS + POP_MS + 150;
const FLASH_OUT_MS = 700;
const CHECK_ANIMATION_MS = FLASH_OUT_START_MS + FLASH_OUT_MS;
const SHAPE_CENTER = 'translate(115.5 108)';
const SHAPE_CENTER_INVERSE = 'translate(-115.5 -108)';

const clampUnit = (value: number): number => Math.min(Math.max(value, 0), 1);

/**
 * Lets each running loop animation play out its current cycle at a higher speed and resolves
 * once all of them are at rest. Cancelled animations count as finished.
 */
export async function finishRunningCycle(animations: Animation[]): Promise<void> {
  for (const animation of animations) {
    const currentIteration = animation.effect?.getComputedTiming().currentIteration ?? 0;
    animation.effect?.updateTiming({ iterations: currentIteration + 1 });
    animation.updatePlaybackRate(FINISH_PLAYBACK_RATE);
  }

  await Promise.all(animations.map((animation) => animation.finished.catch(() => animation)));
}

/** Undoes `finishRunningCycle` on animations that are still mounted. */
export function restoreRunningCycle(animations: Animation[]): void {
  for (const animation of animations) {
    animation.effect?.updateTiming({ iterations: Infinity });
    animation.updatePlaybackRate(1);
  }
}

interface CheckmarkMorphOptions {
  /** Whether the Spark flash fades back out (`true`) or the overlay colour stays. */
  flashFadesOut: Signal<boolean>;
  /** Called once the checkmark has fully formed. */
  onFormed: () => void;
}

/**
 * Drives the logo-to-checkmark morph: the bars morph over 500ms, the checkmark pops, and the
 * colour overlay fades in (and, for a flash, back out). Under reduced motion, or where SVG
 * paths can't be measured, the checkmark appears in its end state straight away.
 */
export function useCheckmarkMorph({ flashFadesOut, onFormed }: CheckmarkMorphOptions) {
  const document = inject(DOCUMENT);

  const morph = signal<LoaderMorph | null>(null);
  const elapsed = signal(0);
  let frame: number | null = null;

  const progress = computed(() => (morph() ? easeInOutCubic(clampUnit(elapsed() / MORPH_MS)) : 1));

  const pathFor = (bar: keyof LoaderMorph, checkStroke: string): string => {
    const currentMorph = morph();
    const currentProgress = progress();

    return currentMorph && currentProgress < 1 ? interpolatePath(currentMorph[bar], currentProgress) : checkStroke;
  };

  const stop = (): void => {
    if (frame !== null) {
      cancelAnimationFrame(frame);
      frame = null;
    }
  };

  inject(DestroyRef).onDestroy(stop);

  const start = (): void => {
    stop();

    const reducedMotion = document.defaultView?.matchMedia?.('(prefers-reduced-motion: reduce)').matches ?? false;
    const nextMorph = reducedMotion ? null : createLoaderMorph(document);

    morph.set(nextMorph);
    elapsed.set(0);

    if (!nextMorph) {
      onFormed();
      return;
    }

    let startTime: number | null = null;
    let formed = false;

    const step = (now: number): void => {
      startTime ??= now;
      elapsed.set(now - startTime);

      if (!formed && elapsed() >= MORPH_MS) {
        formed = true;
        onFormed();
      }

      frame = elapsed() < CHECK_ANIMATION_MS ? requestAnimationFrame(step) : null;
    };

    frame = requestAnimationFrame(step);
  };

  return {
    start,
    stop,

    topPath: computed(() => pathFor('top', CHECK_LONG_STROKE)),
    bottomPath: computed(() => pathFor('bottom', CHECK_SHORT_STROKE)),

    transform: computed(() => {
      const popProgress = (elapsed() - MORPH_MS) / POP_MS;
      const isPopping = morph() !== null && popProgress > 0 && popProgress < 1;
      const scale = isPopping ? 1 + POP_SCALE * Math.sin(Math.PI * popProgress) : 1;

      return `${SHAPE_CENTER} scale(${scale.toFixed(3)}) ${SHAPE_CENTER_INVERSE}`;
    }),

    overlayOpacity: computed(() => {
      const fadesOut = flashFadesOut();

      if (!morph()) {
        return fadesOut ? 0 : 1;
      }

      const fadeIn = clampUnit(elapsed() / FLASH_IN_MS);

      return fadesOut ? Math.min(fadeIn, 1 - clampUnit((elapsed() - FLASH_OUT_START_MS) / FLASH_OUT_MS)) : fadeIn;
    }),
  };
}
